import { useMemo } from 'react';
import { Table } from 'antd';

// ─────────────────────────────────────────────────────────────────
// Glass-i — ErpGridSummaryFooter
//
// Renders a one-row footer inside AntD's `<Table summary />` slot
// that aggregates the columns marked with `aggregate: 'sum' | 'count'
// | 'avg' | 'min' | 'max'`. Two data sources are supported:
//
//   1. Server-supplied summary — the `summary` field on
//      `GridDataResponse` is a map keyed by column-key or dataIndex.
//      If present, that value wins (already-computed, possibly
//      pre-tax-totals from the API).
//   2. Client-side compute — when no server summary is provided and
//      the column has `aggregate`, we compute over `pagedData` (i.e.
//      the rows currently visible) so the footer reflects what the
//      user sees. We deliberately do NOT compute over the full
//      dataSource — server-mode grids never have full data on the
//      client.
//
// The footer renders only when at least one visible column has either
// `aggregate` or a server-supplied summary value.
// ─────────────────────────────────────────────────────────────────

/** @typedef {import('./gridTypes').GridColumn} GridColumn */
/** @typedef {import('./gridTypes').GridAggregateKind} GridAggregateKind */

function readDataIndex(record, dataIndex) {
  if (!record || dataIndex == null) return undefined;
  if (Array.isArray(dataIndex)) {
    let cur = record;
    for (const k of dataIndex) {
      if (cur == null) return undefined;
      cur = cur[k];
    }
    return cur;
  }
  return record[dataIndex];
}

function aggregateValue(kind, rows, dataIndex) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const nums = rows
    .map((r) => readDataIndex(r, dataIndex))
    .filter((v) => typeof v === 'number' && Number.isFinite(v));
  switch (kind) {
    case 'count':
      return rows.length;
    case 'sum':
      return nums.reduce((a, b) => a + b, 0);
    case 'avg':
      if (nums.length === 0) return null;
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min':
      return nums.length === 0 ? null : Math.min(...nums);
    case 'max':
      return nums.length === 0 ? null : Math.max(...nums);
    default:
      return null;
  }
}

function defaultRender(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    // Indian thousand separators look at-home in ERP totals; rounding
    // to 2 decimals matches the ledger format. Columns can override
    // by providing `aggregateRender`.
    if (Number.isInteger(value)) return value.toLocaleString('en-IN');
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  return String(value);
}

/**
 * @param {{
 *   columns: GridColumn[],           — flat leaf columns (already pruned of hidden)
 *   pagedData: any[],                 — rows visible on the current page
 *   summary?: Object<string,any>,    — server-provided summary keyed by column key/dataIndex
 *   selectionColumnWidth?: number,   — width of the AntD selection column for padding
 *   hasExpandableColumn?: boolean
 * }} props
 */
export default function ErpGridSummaryFooter({
  columns,
  pagedData,
  summary,
  selectionColumnWidth = 44,
  hasExpandableColumn = false
}) {
  const anyAggregate = useMemo(() => {
    const hasServer = summary && typeof summary === 'object' && Object.keys(summary).length > 0;
    if (hasServer) return true;
    return (columns || []).some((c) => Boolean(c && c.aggregate));
  }, [columns, summary]);

  if (!anyAggregate) return null;

  // Cell index 0 is the AntD selection-checkbox column (rendered by
  // AntD itself when rowSelection is configured). We span ours
  // starting at index 0 with an empty cell to align under it.
  // Expandable adds another leading column at index 0 too.
  const leadingCells = [];
  if (hasExpandableColumn) {
    leadingCells.push(
      <Table.Summary.Cell index={leadingCells.length} key="__expand__" />
    );
  }
  // Selection column — only when one is present (we can't easily tell
  // from this component, so we always reserve it via a prop the grid
  // sets when rowSelection is on).
  // Caller passes `selectionColumnWidth: 0` to suppress.
  if (selectionColumnWidth > 0) {
    leadingCells.push(
      <Table.Summary.Cell index={leadingCells.length} key="__sel__" />
    );
  }

  return (
    <Table.Summary fixed>
      <Table.Summary.Row className="erp-grid__summary-row">
        {leadingCells}
        {(columns || []).map((col, i) => {
          const key = col.key || col.dataIndex || `col-${i}`;
          const fromServer =
            summary && typeof summary === 'object'
              ? summary[key] ?? summary[col.dataIndex]
              : undefined;
          let cellNode = null;
          if (typeof col.aggregateRender === 'function') {
            cellNode = col.aggregateRender(pagedData, summary);
          } else if (fromServer !== undefined) {
            cellNode = defaultRender(fromServer);
          } else if (col.aggregate) {
            const computed = aggregateValue(col.aggregate, pagedData, col.dataIndex);
            cellNode = defaultRender(computed);
          }
          // The first non-leading cell prefixes a small "TOTAL" label
          // when there's no other content there, so the footer still
          // reads as a totals row even when leftmost columns aren't
          // numeric.
          if (i === 0 && (cellNode === null || cellNode === undefined)) {
            cellNode = (
              <span className="erp-grid__summary-label">TOTAL</span>
            );
          }
          return (
            <Table.Summary.Cell
              index={leadingCells.length + i}
              key={key}
              align={col.align || (col.aggregate ? 'right' : undefined)}
              className="erp-grid__summary-cell"
            >
              {cellNode}
            </Table.Summary.Cell>
          );
        })}
      </Table.Summary.Row>
    </Table.Summary>
  );
}
